'use client'

import { useEffect } from 'react'

/**
 * ⚠️ Ne PLUS enregistrer de service worker.
 *
 * L'ancien SW (`addEventListener('fetch', () => {})`) cassait le chargement
 * de toutes les balises <img> sous Chrome (site entièrement sans images).
 * Ce composant désinscrit désormais TOUT service worker existant et purge
 * les caches, pour réparer les clients qui ont encore l'ancien SW actif.
 *
 * La PWA reste installable sous Chrome moderne sans SF avec fetch handler ;
 * et de toute façon afficher les images prime sur le prompt d'install.
 */
export default function PwaRegister() {
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!('serviceWorker' in navigator)) return
    ;(async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations()
        await Promise.all(regs.map((r) => r.unregister()))
      } catch { /* */ }
      try {
        if ('caches' in window) {
          const keys = await caches.keys()
          await Promise.all(keys.map((k) => caches.delete(k)))
        }
      } catch { /* */ }
    })()
  }, [])

  // ⚠️ ANTI-CRASH APRÈS REDÉPLOIEMENT (téléphone + tablette). Quand on rebuild
  // le site, les anciens chunks JS (/_next/static/…) disparaissent. Un client qui
  // a encore l'ancienne page en cache demande alors un chunk qui n'existe plus →
  // « ChunkLoadError » → écran blanc / l'appli plante (la WebView de l'APK se
  // ferme). Ici on INTERCEPTE cette erreur précise et on RECHARGE la page UNE
  // fois (garde anti-boucle 30 s via sessionStorage) → la WebView récupère le
  // HTML + les chunks frais du serveur au lieu de mourir. Personne ne reste
  // bloqué sur un écran cassé après une mise à jour.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const isChunkError = (msg?: string, name?: string): boolean => {
      const m = (msg || '').toLowerCase()
      return name === 'ChunkLoadError' ||
        m.includes('loading chunk') ||
        m.includes('loading css chunk') ||
        m.includes('failed to fetch dynamically imported module') ||
        m.includes('importing a module script failed') ||
        m.includes("'text/html' is not a valid javascript mime type")
    }
    const recover = () => {
      try {
        const KEY = 'blc.chunkReloadAt'
        const last = Number(sessionStorage.getItem(KEY) || '0')
        if (Date.now() - last < 30000) return // déjà rechargé récemment → pas de boucle
        sessionStorage.setItem(KEY, String(Date.now()))
      } catch { /* sessionStorage indispo → on recharge quand même une fois */ }
      try { window.location.reload() } catch { /* */ }
    }
    const onError = (e: ErrorEvent) => {
      if (isChunkError(e.message, (e.error as any)?.name)) recover()
    }
    const onRejection = (e: PromiseRejectionEvent) => {
      const r: any = e.reason
      if (isChunkError(typeof r === 'string' ? r : r?.message, r?.name)) recover()
    }
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])

  // Désactive DÉFINITIVEMENT le pull-to-refresh natif de l'APK (SwipeRefreshLayout).
  // L'utilisateur ne veut PLUS de rechargement automatique au scroll/glissé (il a
  // un bouton « Recharger » à la place). No-op hors APK (pont `window.AndroidApp`).
  useEffect(() => {
    try { (window as any).AndroidApp?.setPullToRefresh?.(false) } catch { /* pont absent */ }
  }, [])

  return null
}
